import type maplibregl from 'maplibre-gl';
import type { LayerConfig } from './layers/registry';
import { evaluateVisibility } from './layers/visibility';
import type { MapState } from './state/mapState';
import { isMapAlive } from './isMapAlive';

export class LayerManager {
  private initialized = false;
  // AK-193 (ADR-037 #6 / audit F7): last visibility we pushed per layer.
  // applyState is invoked on EVERY store dispatch (whole-store subscribe in
  // v2.tsx); without a diff it re-pushed setLayoutProperty for all ~25 layers
  // on each one (debounced persist, unrelated slices, etc.). We now skip
  // layers whose computed visibility is unchanged — a no-op map mutation that
  // still costs a style-diff pass inside MapLibre.
  private lastVisibility = new Map<string, 'visible' | 'none'>();

  constructor(
    private map: maplibregl.Map,
    private registry: LayerConfig[],
  ) {}

  init(): void {
    if (this.initialized) return;
    const sorted = [...this.registry].sort((a, b) => a.z - b.z);
    for (const cfg of sorted) {
      this.map.addLayer({
        id: cfg.id,
        type: cfg.kind,
        source: cfg.source,
        ...(cfg.sourceLayer ? { 'source-layer': cfg.sourceLayer } : {}),
        ...(cfg.filter ? { filter: cfg.filter } : {}),
        ...(cfg.paint ? { paint: cfg.paint } : {}),
        ...(cfg.layout ? { layout: cfg.layout } : {}),
        ...(cfg.minzoom !== undefined ? { minzoom: cfg.minzoom } : {}),
        ...(cfg.maxzoom !== undefined ? { maxzoom: cfg.maxzoom } : {}),
      } as maplibregl.LayerSpecification);
    }
    this.initialized = true;
  }

  applyState(state: MapState): void {
    // P0 (AK-343): the whole-store subscription in v2.tsx re-invokes applyState
    // on every dispatch, and a dispatch can still land during unmount teardown
    // AFTER `map.remove()` (controller deactivations fire store setters). Bail
    // if the map is gone — `getLayer`/`setLayoutProperty` would throw on a
    // nulled style and bubble to the error boundary.
    if (!isMapAlive(this.map)) return;
    // Sprint 2026-21 T6 (spec §6.4): registry is now declarative-only.
    // Visibility comes from a pure predicate per layer; dynamic paint/layout
    // overrides were removed. Any runtime concern (filter sync, source URL
    // swap) lives in MapPageV2 effects, not the registry.
    for (const cfg of this.registry) {
      if (!this.map.getLayer(cfg.id)) continue;
      const next = evaluateVisibility(cfg.visibility, state) ? 'visible' : 'none';
      // AK-193 F7: skip the setLayoutProperty when visibility didn't change.
      if (this.lastVisibility.get(cfg.id) === next) continue;
      this.map.setLayoutProperty(cfg.id, 'visibility', next);
      this.lastVisibility.set(cfg.id, next);
    }
  }

  destroy(): void {
    // P0 (AK-343): destroy() runs during MapPageV2 unmount. If another effect
    // cleanup already called `map.remove()` (order is not guaranteed under fast
    // navigation like /map → /team), the map's style is nulled and
    // `getLayer`/`removeLayer` throw `Cannot read properties of null` →
    // React error boundary «Что-то пошло не так». MapLibre already frees every
    // layer/source on remove(), so there is nothing to clean up here anyway.
    if (isMapAlive(this.map)) {
      for (const cfg of this.registry) {
        if (this.map.getLayer(cfg.id)) {
          this.map.removeLayer(cfg.id);
        }
      }
    }
    this.lastVisibility.clear();
    this.initialized = false;
  }
}
